"use client";

// Reps : écran "plus de clés" (bloque AU DÉMARRAGE, jamais en cours de leçon).
// Voix BeBot (tutoiement) -> illustration BeBot. Issues : attendre minuit (décompte
// live), racheter une clé avec des fragments, ou passer à l'illimité.
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

function formatCountdown(targetIso) {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "0s";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
}

export default function RepsOutOfKeys({ keys, onBought }) {
    const t = useTranslations();
    const [buying, setBuying] = useState(false);
    const [, setTick] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const fragments = keys && keys.fragments != null ? keys.fragments : 0;
    const cost = keys && keys.buyKeyCost != null ? keys.buyKeyCost : 150;
    const cap = keys && keys.buyDailyCap != null ? keys.buyDailyCap : 2;
    const buyRemaining = keys && keys.buyDailyRemaining != null ? keys.buyDailyRemaining : cap;
    const enough = fragments >= cost;
    const capReached = buyRemaining <= 0;
    const canBuy = enough && !capReached;

    async function buy() {
        setBuying(true);
        try {
            const res = await fetch("/api/reps/keys-buy", { method: "POST" });
            const data = await res.json();
            if (!res.ok || !data.success) {
                if (data.reason === "daily_cap") {
                    toast.error(t("reps_keys_buy_cap_reached", { cap }));
                } else {
                    toast.error(t("reps_keys_buy_not_enough", { have: fragments, cost }));
                }
                return;
            }
            toast.success(t("reps_keys_bought"));
            onBought(data.keys);
        } catch (e) {
            toast.error(t("reps_keys_buy_not_enough", { have: fragments, cost }));
        } finally {
            setBuying(false);
        }
    }

    return (
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 py-10 text-center">
            <img
                src="/images/bebot/bebot-reps.png"
                alt="BeBot"
                className="mb-5 h-40 w-40 object-contain"
            />
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                {t("reps_keys_empty_title")}
            </h1>
            <p className="mb-1 max-w-md text-sm text-gray-500 dark:text-grayDark-400">
                {t("reps_keys_empty_message")}
            </p>
            {keys && keys.nextResetAt && (
                <p className="mb-7 flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                    <SolarIcon name="key-bold" />
                    {t("reps_keys_next_in", {
                        time: formatCountdown(keys.nextResetAt),
                    })}
                </p>
            )}

            <div className="flex w-full max-w-xs flex-col gap-3">
                <button
                    type="button"
                    onClick={buy}
                    disabled={buying || !canBuy}
                    className="flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <SolarIcon name="key-bold" />
                    {buying ? t("reps_keys_buying") : t("reps_keys_buy_cta", { cost })}
                </button>
                {capReached ? (
                    <p className="-mt-1 text-xs text-gray-400">
                        {t("reps_keys_buy_cap_reached", { cap })}
                    </p>
                ) : (
                    !enough && (
                        <p className="-mt-1 text-xs text-gray-400">
                            {t("reps_keys_buy_not_enough", { have: fragments, cost })}
                        </p>
                    )
                )}

                <Link
                    href={{
                        pathname: "/pricing",
                        query: { checkout: "essential" },
                    }}
                    className="flex items-center justify-center gap-2 rounded-xl border border-orange-200 px-4 py-3 text-sm font-semibold text-orange-600 transition-colors hover:bg-orange-50 dark:border-orange-500/40 dark:hover:bg-orange-500/10"
                >
                    <SolarIcon name="infinity-bold" />
                    {t("reps_keys_go_unlimited")}
                </Link>

                <Link
                    href="/reps"
                    className="mt-1 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-grayDark-300"
                >
                    {t("reps_keys_leave")}
                </Link>
            </div>
        </div>
    );
}
